// lib/proposals.ts — WHAT A PENDING PROPOSAL IS, IN ONE PLACE.
//
// G-19 (the 2026-09-11 pre-test sweep): every proposal a forwarded breakdown
// raises landed on the Review screen, and on a CallToWrap build the router
// would not even open that screen — `SCREEN_OWNERS.review` named the personal
// pack alone, so `#/review` was rewritten to Today and the queue had NO door
// at all. Today now carries the same rows, which means two screens read the
// same proposal and must say the SAME WORDS about it.
//
// So the vocabulary lives here rather than inside either screen: the kind's
// name, the one line that says what was read, and the aboutness a household
// row needs. Review.tsx imports `proposalKindLabel` from here — it used to
// own a private copy, and a private copy is how the two screens would have
// drifted the first time a target type was added.

import type { Proposal } from './api';
import { comingUpDayLabel, dayOf, timeOf } from './calendarItems';

/** The kind, in a person's words. An unknown target type is returned as
 * itself: a proposal nobody has named here is still a decision waiting, and
 * hiding it would be the worse half of both. */
export function proposalKindLabel(targetType: string): string {
  switch (targetType) {
    case 'email_draft':
      return 'Drafted reply';
    case 'commitment':
      return 'Extracted commitment';
    case 'commitment_close':
      return 'Commitment closure';
    case 'actor_capture':
      return 'Capture';
    case 'intake_triage':
      return 'New enquiry';
    case 'client_work':
      return 'Work from a client';
    case 'client_letter':
      return 'A letter to a client';
    // Publish PC2 (main) — a post in the pen.
    case 'post_document':
      return 'Post';
    // Chunk PRG-W — a programme, proposed from a plan (SPEC.md's API
    // CONTRACT: propose.ts's kind/target_type).
    case 'programme':
      return 'A programme';
    // GAP 63 — a segment broadcast, one approval, one send.
    case 'segment_broadcast':
      return 'Broadcast';
    // atlas's brief 2026-10-12 (d1cd7c74) — the assistant's change to a Google
    // event with guests; and a note asking an event's organizer for a change.
    case 'google_event_change':
      return 'Calendar change';
    case 'google_event_organizer_ask':
      return 'Note to an organizer';
    default:
      return targetType;
  }
}

/** WHAT WAS READ, in one line.
 *
 * The capture path writes its own `proposed.summary` — it is the sentence the
 * Mail screen already shows beside an arrival ("Waiting on Today — …"), and
 * the same string Review renders inside the proposed record. Falling back
 * through the rationale to the kind's name means this never returns an empty
 * row: a decision with nothing said about it is exactly the thing a person
 * must not be asked to approve. */
// Chunk TODAY-W: Today's `judgment.proposals` rows (lib/api.ts's own
// `TodayJudgmentProposal`) are a narrower cut of the same `proposals` row
// Review's `Proposal` carries — same id/kind/targetType/proposed/rationale,
// no sourceRef/status/decision fields the briefing has no use for. Typed
// here as the minimal shape this function actually reads, so BOTH the full
// Proposal and Today's narrower row satisfy it structurally, and the one
// line of "what was read" logic stays written once.
type ProposalSummaryInput = Pick<Proposal, 'proposed' | 'rationale' | 'targetType'>;

export function proposalSummaryLine(p: ProposalSummaryInput): string {
  const proposed = p.proposed as Record<string, unknown>;
  const summary = typeof proposed['summary'] === 'string' ? proposed['summary'].trim() : '';
  if (summary) return summary;
  const title = typeof proposed['title'] === 'string' ? proposed['title'].trim() : '';
  if (title) return title;
  const rationale = (p.rationale ?? '').trim();
  if (rationale) return rationale;
  return proposalKindLabel(p.targetType);
}

/** ⚑ THE DOCUMENT NOBODY READ, IN THE ONE SENTENCE THE SERVER WROTE FOR IT.
 *
 * `sourceRef.parseState` is the server's own word for what happened to the
 * reading, and there are exactly two values a family can be handed:
 *   · 'not_included' — the plan does not cover reading this kind of document.
 *     It was kept in full; nothing is coming back for it.
 *   · 'deferred' — nothing has read it YET. Either the day's reading was used
 *     up, or the reading could not run at all (a model the box could not
 *     reach). The nightly catch-up comes back for it.
 *
 * Both are STATEMENTS ABOUT A FILE, not interpretations of contents nothing
 * has looked at — which is why callers render this instead of the
 * interpretation label, never beside it. Returning null means the document was
 * actually read and the ordinary rationale applies.
 *
 * ⚑ ONE HELPER, THREE SCREENS. Today, Review and Mail all answer the same
 * question about the same proposal, and the Day 3 walk found the answer living
 * on exactly one of them. */
export function captureUnreadLine(p: Proposal): string | null {
  const ref = (p.sourceRef ?? {}) as Record<string, unknown>;
  const state = ref['parseState'];
  if (state !== 'not_included' && state !== 'deferred') return null;
  const rationale = (p.rationale ?? '').trim();
  return rationale.length > 0 ? rationale : null;
}

/** WHOSE IT IS, when the proposal says so.
 *
 * A capture records the subject it was pressed on (`proposed.context`, the
 * pack's own `captureSubjects` vocabulary — `aa_subject` is one of the five
 * actor_assist declares). Mail-raised proposals carry NO context at all, and
 * that absence is an answer rather than a gap: a breakdown forwarded to the
 * household address is the household's until somebody reads it, so the row
 * claims no owner. Never guessed from the body — a precise, confident, false
 * statement about whose career this is would be worse than silence. */
export function proposalSubjectId(p: Proposal): string | null {
  const context = (p.proposed as { context?: unknown }).context;
  if (!context || typeof context !== 'object') return null;
  const { subjectType, subjectId } = context as { subjectType?: unknown; subjectId?: unknown };
  return subjectType === 'aa_subject' && typeof subjectId === 'string' && subjectId ? subjectId : null;
}

/** ⚑ THE KINDS WHOSE DECISION NEEDS THE CARD, NOT A ROW.
 *
 * Review's own rule, carried here so a list screen cannot break it: APPROVE
 * MUST NEVER COMMIT CONTENT THE SCREEN DID NOT SHOW. These four are decided
 * by editing what will be sent — a reply's body, a letter's timing, an
 * enquiry's fork, a client's title — and the generic approve door would send
 * the model's untouched draft. A Today row for one of these offers the way in
 * instead of the two acts.
 *
 * All four are the cockpit's today; none is raised on a household build. They
 * are named anyway, because the day one is, the failure would be a message
 * sent to an outside party that nobody read first. */
export const DECIDED_ON_ITS_OWN_CARD: readonly string[] = [
  'email_draft',
  'intake_triage',
  'client_work',
  'client_letter',
  // GAP 63 amendment 6 — the Review card: counts by reason, the rendered
  // sample, channel + sending identity, before anyone taps Approve. A row
  // with no card would send a model-rendered email to a whole segment on
  // the strength of a one-line summary.
  'segment_broadcast',
  // A change to a Google event reaches its guests: approving needs the
  // person's answer to "Tell the guests?", which only the card asks.
  'google_event_change',
  // A note to someone else's address — read before anything is done with it.
  'google_event_organizer_ask',
];

export function decidedOnItsOwnCard(p: Proposal): boolean {
  return DECIDED_ON_ITS_OWN_CARD.includes(p.targetType);
}

// ═══════════════════════════════════════════════════════════════════════════
// WHAT A CAPTURE PROPOSAL SAYS, IN WORDS — the Day 2 walk's S3.3 blocker.
// ---------------------------------------------------------------------------
// Review rendered a capture through its GENERIC key/value card, which meant a
// parent opening the first thing that ever lands after "forward it to us" read
// this, verbatim:
//
//   actions: [{"op":"createRecord","fields":{…}}]   context: {}
//   sourceIds: []   captureKind: generic_inbox
//
// Those four keys are the ENGINE's, and three of them are plumbing. The one
// sentence the screen owed — what was read, from what, and what it will become
// — was buried mid-dump as a `summary:` key. So the vocabulary lives here,
// beside `proposalSummaryLine`, for the reason this file's own header gives:
// Today and Review both read the same proposal and must say the same words
// about it, and a private copy is how two screens drift.
// ═══════════════════════════════════════════════════════════════════════════

/** What a created record IS, in the family's words.
 *
 * ⚑ MOVED HERE FROM HouseholdMail.tsx, unchanged. Mail said "Became an
 * audition — Northwind" while Review printed `{"recordType":"aa_audition"}`
 * about the same proposal; one of those two screens was a database leaking. */
const BECAME_NOUN: Record<string, string> = {
  aa_audition: 'an audition',
  aa_shoot_day: 'a shoot day',
  aa_project: 'a project',
  aa_role: 'a role',
  aa_engagement: 'a booking',
  aa_session: 'a session',
  aa_payment: 'a payment',
  aa_expense: 'an expense',
  aa_credit: 'a credit',
  aa_subject: 'a performer',
  aa_tape: 'a tape',
  aa_source: 'a filed document',
  aa_contact: 'a contact',
  aa_person: 'a person',
  aa_crew_credit: 'a crew position',
  aa_training: 'a class',
  aa_award: 'an award',
  aa_skill: 'a skill',
  aa_document: 'a document',
};

/** The type key as a last resort, said as words rather than shown raw. A
 * record type nobody has named here is still a record the family created, and
 * `aa_side` on a screen is a database leaking. */
export function becameNoun(recordType: string): string {
  return BECAME_NOUN[recordType] ?? recordType.replace(/^aa_/, '').replace(/_/g, ' ');
}

/** A record's own display name, without a separator left hanging off the end.
 *
 * ⚑ THE A4 LESSON, ONE LAYER OVER. A pack's name template can produce a name
 * whose last field is empty — `{modality} — {submitted_at}` on an audition with
 * no submission date is literally "self_tape —" — and printing that after
 * "Became an audition" gives a family a sentence that ends mid-thought. The
 * record keeps its name; this row does not repeat the dangle. */
export function tidyName(name: string): string {
  return name.replace(/[\s–—·|,-]+$/u, '').trim();
}

/** The fields a pack actually names a record by, in the order a person would
 * read them. Deliberately a SHORT list of generic field names rather than the
 * pack's name templates: this is a preview beside a button, not the record's
 * identity, and an unnamed line ("a person") is honest where a guessed one is
 * not.
 *
 * ⚑ B13 — `character_name` JOINS THIS LIST for the reason `title` and
 * `full_name` are already here: `aa_role`'s own `name_template` is the single
 * field `{character_name}` (recordTypes.ts), so listing the field IS
 * rendering the template — the same generalisation this list already makes
 * for a project's `title`. Reading it under its own wrong-but-frozen name
 * (recordTypes.ts's own header explains why it can never be renamed, D12) is
 * still the part's actual name; the review card owed that, not a database
 * key. Without it a captured breakdown's role line read "a role" with
 * nothing after it — the walk's own S2 finding. */
const NAME_FIELDS = ['title', 'full_name', 'name', 'day_label', 'character', 'character_name', 'position', 'kind'];

function recordName(fields: Record<string, unknown> | undefined): string {
  if (!fields) return '';
  for (const key of NAME_FIELDS) {
    const value = fields[key];
    if (typeof value === 'string' && value.trim()) return tidyName(value);
  }
  return '';
}

/** ⚑ B13 §2 — AN AUDITION NAMES ITSELF BY WHAT IS OWED, NOT BY A FIELD.
 *
 * `aa_audition` has no name field at all — its own `name_template` is
 * `{modality} — {submitted_at}` (a raw ISO timestamp on a fresh capture,
 * AuditionSpread.tsx's own header calls it "unreadable as a headline") — so
 * `recordName`'s generic field list can never answer for it. The two facts a
 * family is actually approving are WHAT KIND OF READ and WHEN IT IS DUE, so
 * this reads those two off the proposal's own fields instead of a name.
 *
 * ⚠ ONE WORDING, ONE FORMATTER. The "self-tape due" / "due" phrasing and the
 * day label are the exact ones `calendarItems.ts`'s own due-date row and
 * Today's "Coming up" use — a second phrasing here is how the two screens
 * would say a different sentence about the same deadline, which is the
 * mistake this file's own header exists to prevent. Returns '' (no name)
 * when the proposal carries no due date to say a due date with — the model's
 * summary line still stands, and a bare "an audition" is honest where a
 * fabricated deadline would not be. */
function auditionName(fields: Record<string, unknown> | undefined): string {
  if (!fields) return '';
  const day = dayOf(fields['due_at']);
  if (!day) return '';
  const modality = typeof fields['modality'] === 'string' ? fields['modality'] : undefined;
  // Same rule calendarItems.ts's own due row states: A TAPE IS DUE, A ROOM
  // IS SCHEDULED — the modality only enters the phrase where it names the
  // thing being handed in.
  const owed = modality === 'self_tape' ? 'self-tape due' : 'due';
  const time = timeOf(fields['due_at']);
  return `${owed} ${comingUpDayLabel(day)}${time ? ` · ${time}` : ''}`;
}

/** B16 §4 — A COMMITMENT'S OWN DUE LINE, in the SAME wording
 * `auditionName` above already uses for the identical fact (one due-date
 * phrasing across this file, not a second one grown for a second kind):
 * "Due Fri, Oct 2 · 5:00 PM", or "No date given" when the proposal carries
 * none to say one with. Never a fabricated deadline. */
export function commitmentDueLine(dueAtRaw: unknown): string {
  const day = dayOf(dueAtRaw);
  if (!day) return 'No date given';
  const time = timeOf(dueAtRaw);
  return `Due ${comingUpDayLabel(day)}${time ? ` · ${time}` : ''}`;
}

/** WHAT APPROVING WILL CREATE, one line per record, in the family's words.
 *
 * ⚠ LINKS ARE NOT LISTED. A `link` action is how two staged records find each
 * other; it is not a thing a person gets. Naming them would pad the list with
 * rows nobody can act on. */
export function captureOutcomeLines(p: Proposal): string[] {
  const actions = (p.proposed as Record<string, unknown>)['actions'];
  if (!Array.isArray(actions)) return [];
  const lines: string[] = [];
  for (const raw of actions) {
    if (typeof raw !== 'object' || raw === null) continue;
    const action = raw as { op?: string; recordType?: string; fields?: Record<string, unknown> };
    if (action.op !== 'createRecord' || typeof action.recordType !== 'string') continue;
    // B16 re-walk — a Knot capture's OWN retained copy of the words is not a
    // thing approving creates for anyone; listed, it read "a filed document —
    // other" under a note, hiding "Kept as a note — nothing else is created."
    // A forwarded message's retained original is still listed (it IS a
    // document the family can open).
    if (
      action.recordType === 'aa_source' &&
      action.fields?.['source_note'] === 'Knot capture (inbox)' &&
      (p.sourceRef ?? {})['sourceType'] !== 'inbound_mail'
    )
      continue;
    const noun = becameNoun(action.recordType);
    const name = action.recordType === 'aa_audition' ? auditionName(action.fields) : recordName(action.fields);
    lines.push(name ? `${noun} — ${name}` : noun);
  }
  return lines;
}

/** WHERE IT CAME FROM, when the proposal knows. Mail says it in the two terms
 * a family can tell apart (one they forwarded to, one they connected); a
 * capture made with somebody standing over it says nothing, because they were
 * there. */
export function captureProvenanceLine(p: Proposal): string | null {
  const ref = p.sourceRef ?? {};
  if (ref['sourceType'] !== 'inbound_mail') return null;
  const carrier =
    ref['carrier'] === 'connected_mailbox'
      ? 'From the mailbox you connected'
      : ref['carrier'] === 'forwarded'
        ? 'Forwarded to your address'
        : 'Arrived by mail';
  const subject = typeof ref['subject'] === 'string' ? tidyName(ref['subject']) : '';
  const from = typeof ref['from'] === 'string' ? ref['from'].trim() : '';
  const parts = [carrier];
  if (subject) parts.push(`“${subject}”`);
  if (from) parts.push(`from ${from}`);
  return parts.join(' · ');
}

/** How much of the original the card shows. Long enough to recognise the
 * document, short enough that a decision still fits on a phone above the
 * buttons — the rest is one tap away on the filed original. */
const EXCERPT_CHARS = 280;

/** The shared formatting rule behind `capturePastDueLine`: a date already
 * behind `now` becomes the sentence; anything else (missing, unparseable,
 * still ahead) says nothing. */
function pastDueLine(dueRaw: unknown, now: Date): string | null {
  if (typeof dueRaw !== 'string' || !dueRaw.trim()) return null;
  // ⚑ A DATE WITH NO TIME IS A DAY, NOT A UTC MIDNIGHT (walk, 2026-09-24).
  // `new Date('2026-09-15')` is midnight UTC — 8 p.m. on the 14th in Toronto —
  // so the card said "Due Sep 14" for a notice due the 15th, and would call a
  // deadline "passed" all through its own last day. A bare YYYY-MM-DD is read
  // as the END of that day in the viewer's own zone.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dueRaw.trim());
  const due = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]), 23, 59, 59, 999)
    : new Date(dueRaw);
  if (Number.isNaN(due.getTime()) || due.getTime() >= now.getTime()) return null;
  const formatted = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Due ${formatted} — passed`;
}

/** B3: a proposal that will create something with a due/deadline date
 * already past looks exactly like a current one — a card proposing "…by end
 * of day Tuesday, September 15" seen on the 24th sat beside "4d ago" with
 * nothing to tell them apart. Reads the date off the PROPOSAL'S OWN PAYLOAD,
 * never the text of the rationale — and only a field a pack actually
 * declares as a deadline.
 *
 * Two shapes carry one: a capture's `proposed.actions[*].fields.due_at` —
 * `aa_audition.due_at` is the only deadline-shaped field this pack's record
 * types carry (checked against `recordTypes.ts`); nothing else names one —
 * and, ⚑ found on the B3 walk in a real browser (2026-09-24), a mail-raised
 * commitment (`kind: 'extraction'`, `targetType: 'commitment'`) which has no
 * `actions` array yet at all and carries its date at `proposed.dueAt`
 * directly (`packages/core/src/memory/extraction.ts`'s own INSERT shape).
 * Meaning lives in words (voice canon §2.7), so this returns the whole
 * sentence, not a flag a caller has to word itself. */
export function capturePastDueLine(p: Proposal, now: Date = new Date()): string | null {
  const proposed = p.proposed as Record<string, unknown>;
  const actions = proposed['actions'];
  if (Array.isArray(actions)) {
    for (const raw of actions) {
      if (typeof raw !== 'object' || raw === null) continue;
      const action = raw as { op?: string; recordType?: string; fields?: Record<string, unknown> };
      if (action.op !== 'createRecord' || action.recordType !== 'aa_audition') continue;
      const line = pastDueLine(action.fields?.['due_at'], now);
      if (line) return line;
    }
  }
  if (p.kind === 'extraction' && p.targetType === 'commitment') {
    const line = pastDueLine(proposed['dueAt'], now);
    if (line) return line;
  }
  return null;
}

/** THE WORDS THAT WERE READ. A capture's retained text is the only thing on
 * this card that is the FAMILY'S, and approving without it is approving a
 * reading of something the screen never showed. */
export function captureExcerpt(p: Proposal): string | null {
  const actions = (p.proposed as Record<string, unknown>)['actions'];
  if (!Array.isArray(actions)) return null;
  for (const raw of actions) {
    if (typeof raw !== 'object' || raw === null) continue;
    const fields = (raw as { fields?: Record<string, unknown> }).fields;
    const text = fields && typeof fields['raw_text'] === 'string' ? fields['raw_text'].trim() : '';
    if (!text) continue;
    return text.length > EXCERPT_CHARS ? `${text.slice(0, EXCERPT_CHARS).trimEnd()}…` : text;
  }
  return null;
}
